// "Why would MCO pay when ECO does not?" — the two mechanism columns on one $/acre scale.
// Ported from datanac-core-dbx/products/mco_eco_app_reference/js/comparator.js.
//
// ECO's band, projected price, and trigger come from eco.gold.parameters
// (MCO.dbo.eco_parameters) — never hardcoded, never borrowed from MCO. MCO watches
// margin; ECO watches revenue. Protection is sized separately even when the dollar
// amounts coincide (Custer 2026: both $16,008, unrelated reasons).

import { computeMcoProjectedPayment, roundHalfUp } from "./mcoModel.js";
import { num, numOrNull, pickRow, toShareDecimal } from "./mcoShared.js";

const round2 = (v) => roundHalfUp(v, 2);
const clamp01 = (v) => Math.min(1, Math.max(0, v));

/**
 * Mirror dbo.eco_parameters row → the ECO legs the scenario reads independently.
 * Null if coverage_range, projected_price, trigger_area_revenue, or area_loss_end
 * are missing — do not invent 5% / MCO's price.
 */
export function ecoParamsFromMirrorRow(row) {
  if (!row) return null;
  const coverageRange = num(pickRow(row, "coverage_range"));
  const projectedPrice = num(pickRow(row, "projected_price"));
  const triggerAreaRevenue = numOrNull(pickRow(row, "trigger_area_revenue"));
  const areaLossEnd = num(pickRow(row, "area_loss_end_percent", "area_loss_end"));
  const coverageStart = num(pickRow(row, "coverage_level_percent", "coverage_start"));
  if (!(coverageRange > 0) || !(projectedPrice > 0) || triggerAreaRevenue == null || !(areaLossEnd > 0) || !(coverageStart > 0)) {
    return null;
  }
  return {
    projectedPrice,
    coverageRange,
    coverageStart,
    areaLossEnd,
    expectedAreaYield: numOrNull(pickRow(row, "expected_area_yield")),
    expectedAreaRevenue: numOrNull(pickRow(row, "expected_area_revenue")),
    triggerAreaRevenue,
  };
}

/**
 * The expected county legs both columns are built on — also the slider seeds and the readonly
 * assumption row. Split out of the scenario so the UI can seed its sliders before stressing.
 *
 * @param {object} baseline — stressBaseline from useMco
 */
export function scenarioSeed(baseline) {
  if (!baseline) return null;
  const eay = num(baseline.expectedYield);
  const pp = num(baseline.projectedPrice);
  const er = baseline.expectedRevenue == null ? round2(pp * eay) : round2(num(baseline.expectedRevenue));
  const em = baseline.expectedMargin == null ? null : round2(num(baseline.expectedMargin));
  const projected = num(baseline.projectedCosts);
  // Prefer the Official cost basket; fall back to what the guarantee implies (ER − EM), the same
  // precedence buildMcoCalculatorWalk uses so the two tabs never disagree by a rounding cent.
  const expectedCosts =
    Number.isFinite(projected) && projected !== 0 ? round2(projected) : em == null ? null : round2(er - em);
  return {
    expectedYield: eay,
    projectedPrice: pp,
    expectedCosts,
    expectedRevenue: er,
    // RMA publishes no county expected yield until a few days after the discovery window closes,
    // so before then every threshold on the screen is an estimate and is drawn as one.
    estimates: baseline.expectedYieldBasis !== "official",
  };
}

/** Slider bounds, from the reference comparator's snapSliders(). */
export function scenarioStressBounds(seed) {
  if (!seed) return null;
  return {
    harvestPrice: { min: round2(seed.projectedPrice * 0.75), max: round2(seed.projectedPrice * 1.2), step: 0.01 },
    finalYield: {
      min: roundHalfUp(seed.expectedYield * 0.7, 1),
      max: roundHalfUp(seed.expectedYield * 1.2, 1),
      step: 0.1,
    },
    harvestCosts: {
      min: Math.round(num(seed.expectedCosts) * 0.6),
      max: Math.round(num(seed.expectedCosts) * 2),
      step: 1,
    },
  };
}

/**
 * @param {object} baseline — stressBaseline from useMco (county legs + band geometry + baseline.eco)
 * @param {object[]} units — [{ approvedYield, acres, sharePct, coveragePercent }]; coverage and
 *   share may arrive as percents (80) or decimals (0.8)
 * @param {object} stress — { harvestPrice, finalYield, harvestCosts } — absolute, not multipliers
 */
export function buildEcoMcoScenario(baseline, units, stress) {
  const seed = scenarioSeed(baseline);
  if (!seed) return { ready: false, blockers: ["baseline missing"] };
  // No cost leg means the margin column has no meaning — a $0 basket would draw MCO as ECO's twin.
  if (seed.expectedCosts == null) {
    return { ready: false, blockers: ["expected input cost missing"] };
  }

  const eco = baseline.eco;
  if (!eco || !(eco.coverageRange > 0) || !(eco.projectedPrice > 0) || eco.triggerAreaRevenue == null) {
    return { ready: false, blockers: [baseline.ecoBlocker || "ECO parameters missing"] };
  }

  const mcoPp = seed.projectedPrice;
  const eay = seed.expectedYield;
  const mcoCoverageRange = num(
    baseline.coverageRange ?? round2(num(baseline.coverageStart ?? 0.95) - num(baseline.areaLossEnd ?? 0.86))
  );
  const planCode = String(baseline.planCode || "68");

  const harvestPrice = num(stress?.harvestPrice ?? mcoPp);
  const finalYield = num(stress?.finalYield ?? eay);
  const harvestCosts = num(stress?.harvestCosts ?? seed.expectedCosts);

  const proj = computeMcoProjectedPayment({ ...baseline, finalYield, harvestPrice, harvestCosts });
  if (!proj.ready) return { ready: false, blockers: proj.blockers };
  const r = proj.result;

  // ECO RP §9(b)(1): payment-factor baseline re-expects at max(PP, HP), cents before the ratio.
  // Served trigger_area_revenue is that product at projected; only HP > ECO's own PP recomputes it.
  // Protection stays on ECO's projected price — harvest rise lives in the revenue ratio only.
  const ecoEay = eco.expectedAreaYield > 0 ? num(eco.expectedAreaYield) : eay;
  const ecoRecomputed = harvestPrice > eco.projectedPrice;
  const ecoBase = ecoRecomputed
    ? round2(ecoEay * harvestPrice)
    : eco.expectedAreaRevenue != null
      ? round2(num(eco.expectedAreaRevenue))
      : round2(ecoEay * eco.projectedPrice);
  const ecoTrigger = ecoRecomputed ? round2(ecoBase * num(eco.coverageStart)) : round2(num(eco.triggerAreaRevenue));
  const ecoCover = round2(ecoBase * num(eco.coverageRange));
  const ecoMaxAt = round2(ecoBase * num(eco.areaLossEnd));
  const ecoFactor = ecoCover > 0 ? clamp01((ecoTrigger - r.harvestRevenue) / ecoCover) : 0;

  // Plan 68's protection re-expects at max(PP, HP) — a floor, not a cap (26-MCO §1 Example 4).
  const mcoRecomputed = planCode === "68" && r.harvestPrice > mcoPp;
  const mcoPrice = mcoRecomputed ? Math.max(mcoPp, r.harvestPrice) : mcoPp;

  // One round on the whole product per unit, not per acre: rounding $/ac first drifts the farm
  // total by dollars (the reference app and the Calculator tab both round the full product).
  const rows = (units?.length ? units : [baseline]).map((u, i) => {
    const aph = num(u.approvedYield);
    const acres = num(u.acres);
    const share = toShareDecimal(u.sharePct);
    const coveragePercent = toShareDecimal(u.coveragePercent ?? u.protectionFactor ?? 1);
    const ecoProtection = Math.round(aph * eco.projectedPrice * acres * share * num(eco.coverageRange) * coveragePercent);
    const mcoProtection = Math.round(aph * mcoPrice * acres * share * mcoCoverageRange * coveragePercent);
    return {
      id: u.id ?? i,
      approvedYield: aph,
      acres,
      share,
      coveragePercent,
      ecoProtection,
      mcoProtection,
      ecoPayment: Math.round(ecoFactor * ecoProtection),
      mcoPayment: Math.round(r.paymentFactor * mcoProtection),
    };
  });
  const total = (key) => rows.reduce((sum, row) => sum + row[key], 0);

  return {
    ready: true,
    blockers: [],
    estimates: seed.estimates,
    recomputed: mcoRecomputed,
    ecoRecomputed,
    mcoRecomputed,
    // Readonly assumption row: what the guarantee is (or is expected to be) built on.
    expected: {
      countyYield: eay,
      projectedPrice: mcoPp,
      inputCost: seed.expectedCosts,
      revenue: r.expectedRevenue,
      margin: r.expectedMargin,
      coverageRange: mcoCoverageRange,
    },
    stress: { harvestPrice: r.harvestPrice, finalYield, harvestCosts },
    revenue: {
      current: r.harvestRevenue,
      trigger: ecoTrigger,
      maxAt: ecoMaxAt,
      cover: ecoCover,
    },
    margin: {
      current: r.harvestMargin,
      trigger: r.triggerMargin,
      maxAt: round2(r.triggerMargin - r.coverageValue),
      cover: round2(r.coverageValue),
    },
    eco: { paymentFactor: ecoFactor, protection: total("ecoProtection"), payment: total("ecoPayment") },
    mco: { paymentFactor: r.paymentFactor, protection: total("mcoProtection"), payment: total("mcoPayment") },
    units: rows,
  };
}
